import { Link } from 'react-router-dom';
import { ArrowRight, Clock, MapPin } from 'lucide-react';
import { useCatalog } from '../data/catalog';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { useSiteContent } from '../utils/siteContent';

const COPY = {
  pt: { title: 'Cruzeiros & Excursões em Terra', sub: 'Experiências em terra coordenadas para visitantes de cruzeiro.', empty: 'Ainda não existem excursões em terra publicadas.', note: 'O catálogo será disponibilizado quando os detalhes operacionais forem confirmados.', view: 'Ver excursão' },
  en: { title: 'Cruise & Shore Excursions', sub: 'Locally coordinated shore experiences for cruise visitors.', empty: 'There are no published shore excursions yet.', note: 'The catalogue will be available when operational details are confirmed.', view: 'View excursion' },
  de: { title: 'Kreuzfahrt & Landausflüge', sub: 'Lokal koordinierte Landausflüge für Kreuzfahrtgäste.', empty: 'Es sind noch keine Landausflüge veröffentlicht.', note: 'Der Katalog wird nach Bestätigung der operativen Details veröffentlicht.', view: 'Ausflug ansehen' },
  fr: { title: 'Croisières & Excursions à Terre', sub: 'Expériences à terre coordonnées localement pour les croisiéristes.', empty: 'Aucune excursion à terre n’est encore publiée.', note: 'Le catalogue sera disponible après confirmation des détails opérationnels.', view: 'Voir l’excursion' },
};

export default function CruisePage() {
  const { lang } = useLang();
  const fallback = COPY[lang] || COPY.pt;
  const cms = useSiteContent('cruise');
  const copy = { ...fallback, ...cms, sub: cms.description || fallback.sub, empty: cms.empty_title || fallback.empty, note: cms.empty_description || fallback.note, view: cms.cta_label || fallback.view };
  const { shoreExcursions, shoreExcursionsLoaded } = useCatalog();
  useSEO(`${copy.title} | Eco Collection`, copy.sub);

  return <div data-testid="cruise-page">
    <PageHeader kicker="Eco Collection" title={copy.title} sub={copy.sub} />
    <section className="bg-eco-cream py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {!shoreExcursionsLoaded && <div data-testid="cruise-loading" className="rounded-3xl border border-dashed border-eco-ink/15 bg-white py-16 text-center text-eco-ink/50">…</div>}
        {shoreExcursionsLoaded && shoreExcursions.length === 0 && <div data-testid="cruise-empty" className="rounded-3xl border border-dashed border-eco-ink/15 bg-white px-6 py-16 text-center">
          <h2 className="font-serif text-2xl text-eco-ink">{copy.empty}</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-eco-ink/60">{copy.note}</p>
        </div>}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {shoreExcursions.map((item, index) => <Reveal key={item.id} delay={(index % 3) * 0.08}>
            <Link to={`/cruise/${item.slug}`} data-testid={`cruise-card-${item.slug}`} className="group block overflow-hidden rounded-2xl border border-eco-ink/10 bg-white shadow-sm transition-shadow hover:shadow-lg">
              {item.img && <img src={item.img} alt={item.name} className="h-56 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />}
              <div className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-eco-gold">{item.destination}{item.island ? ` · ${item.island}` : ''}</p>
                <h2 className="mt-2 text-xl font-semibold text-eco-ink">{item.name}</h2>
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-eco-ink/60">
                  {item.port && <span className="flex items-center gap-1.5"><MapPin size={14} /> {item.port}</span>}
                  {item.duration && <span className="flex items-center gap-1.5"><Clock size={14} /> {item.duration}</span>}
                </div>
                {item.highlights?.length > 0 && <p className="mt-4 line-clamp-2 text-sm text-eco-ink/65">{item.highlights.join(' · ')}</p>}
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-eco-green">{copy.view} <ArrowRight size={15} /></span>
              </div>
            </Link>
          </Reveal>)}
        </div>
      </div>
    </section>
  </div>;
}
